// Currently a copy of EventContent. Update with Fundraiser-specific components.

import React, { useEffect, useMemo, useState } from "react";
import LatestDonations from "../ui/latest-donations";
import {
  Event as EventType,
  Community,
  OrgBootstrap,
  User,
} from "@dreambigger/shared/src/types";
import {
  Card,
  Group,
  ShareBar,
  DecoratedImage,
} from "@dreambigger/design-system";
import { add, isBefore, isAfter } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { getDateFnsTimeZone } from "@dreambigger/shared/src/utils";

import CountdownTimer from "../ui/countdown-timer";
import {
  CardDetails,
  CardFundraisingGoal,
} from "@dreambigger/shared/src/components/board/feed-cards/card-components";
import { useRouter } from "next/router";
import { useMixpanel, useGA, useSegment } from "@dreambigger/shared/src/hooks";
import InspireAnalytics, {
  InspireAnalyticEvents,
} from "@dreambigger/shared/src/utils/inspire-analytics-helper";
import {
  EditButton,
  MatchGrantBanner,
  MatchGrantMilestones,
  AnimatedArrows,
  EventThankYouCard,
} from "../ui";

const THANK_YOU_NOTES_BUTTON_TITLE_DEFAULT = "SEND THANK YOU NOTES";
const THANK_YOU_NOTES_TITLE_DEFAULT =
  "Show how much you appreciate your friends who made this project a success!";
type FundraiserContentProps = {
  event: EventType;
  orgBootstrap: OrgBootstrap;
  community: Community;
  coverImage: string;
  user?: User;
};

const FundraiserContent = ({
  event,
  orgBootstrap,
  community,
  coverImage,
  user,
}: FundraiserContentProps) => {
  const router = useRouter();
  const mixpanel = useMixpanel();
  const segment = useSegment();
  const googleAnalytics = useGA();

  const shareAnalyticsProperties = useMemo(
    () => ({
      "Event Type": event.isFundraiser ? "fundraiser" : "event",
      "Event ID": event.id,
      "Event Name": event.name,
      groupId: event.organization.id,
      "Group Name": event.organization.name,
    }),
    [
      event.id,
      event.name,
      event.isFundraiser,
      event.organization.id,
      event.organization.name,
    ]
  );

  //------User Action Menu Variables------
  const fundraiserUrl = `/events/${event.id}/detail`;
  const handleFundraiserClick = () => {
    router.push(fundraiserUrl);
  };
  const donateURL = `/events/${event.id}/donate`;

  // ---------countdown logic---------------
  const currentDate = new Date();
  const eventStartDate = event.startAt
    ? new Date(Date.parse(event.startAt))
    : null;
  const eventNotStarted = eventStartDate
    ? isBefore(currentDate, eventStartDate)
    : null;

  const [disableDonation, setDisableDonation] = useState(false);
  const eventDonationState = event.organization.meta.eventDonationState;
  // Store community keyword. Example: Thrivent prefers to refer to their events as "projects."
  const keyword = community.meta?.marketing?.keywordUpper || "Event ";
  const eventEndDateBuffer =
    event.organization.meta.eventEndDateBuffer > -1
      ? event.organization.meta.eventEndDateBuffer
      : 10;
  useEffect(() => {
    setDisableDonation(eventNotStarted && eventDonationState);
    if (eventNotStarted) {
      setDisableDonation(eventDonationState);
    }
  }, [eventNotStarted, eventDonationState]);

  // Check if event has ended (for Thank You Notes display)
  const eventEnded = useMemo(() => {
    const { endAt } = event;
    // If no end date, treat as not ended (matches legacy moment().isAfter(undefined) === false)
    if (!endAt) return false;
    // Use full timestamp comparison (matches legacy moment().isAfter(data.endAt))
    return isAfter(new Date(), new Date(endAt));
  }, [event]);

  // Check if donation buffer has expired (endAt + buffer days)
  // Donations remain open for N days after the event ends
  const donationBufferExpired = useMemo(() => {
    const { timeZone, endAt } = event;
    if (!timeZone || !endAt) return false;

    const dateFnsTimezone = getDateFnsTimeZone(timeZone);
    const endDatePlusBuffer = add(new Date(endAt), {
      days: eventEndDateBuffer,
    });
    const now = new Date();
    const formattedNow = formatInTimeZone(now, dateFnsTimezone, "yyyy-MM-dd");
    const formattedEnd = formatInTimeZone(
      endDatePlusBuffer,
      dateFnsTimezone,
      "yyyy-MM-dd"
    );
    return isAfter(new Date(formattedNow), new Date(formattedEnd));
  }, [event, eventEndDateBuffer]);

  const handleClick = () => {
    window.location.pathname = window.location.pathname.replace(
      "detail",
      "edit"
    );
  };

  // -------- Main Header ------------
  const MainContentHeaderComponent = useMemo(() => {
    if (!event) return;
    return (
      <>
        <DecoratedImage
          src={coverImage}
          alt="Event Img"
          cornerButtonEnabled={event.isAdmin} // User should be able to edit this picture if user is admin of event.
          cornerButtonText="Edit"
          cornerButtonTextExpanded="Edit Image"
          cornerButtonIcon="images"
          style={{
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            boxShadow: "rgb(0 0 0 / 16%) 0px 1px 4px",
          }}
          className="z-99"
          cornerButtonFunction={handleClick}
        />
      </>
    );
  }, [event]);

  // --------- Share ------------------
  const ShareComponent = useMemo(() => {
    if (!event) return;
    return (
      <>
        <style>
          @import
          url('https://fonts.googleapis.com/css2?family=The+Girl+Next+Door&display=swap');
        </style>
        <h3
          className="pv-3 mb-0 f-4 sm_f-5 md_f-7"
          style={{
            textAlign: "center",
            fontWeight: "bold",
            marginBottom: "20px",
            fontFamily: "The Girl Next Door",
            color: "darkgray",
          }}
        >
          Help spread the word!
        </h3>
        <h3
          className="handwriting mb-0 f-4 sm_f-5 md_f-7"
          style={{
            textAlign: "center",
            fontFamily: "'The Girl Next Door', cursive",
            color: "darkgray",
          }}
        >
          The average share raises $30!
        </h3>
        <div className="w-100 md_w-80 lg_w-70 xl_w-60 self-center">
          {/* ShareBar at non-mobile sizes */}
          <div className="dn md_db">
            <ShareBar
              labeledButtons={true}
              liftOnHover={true}
              rectangularButtons={true}
              clipboard={false}
              analyticsProperties={shareAnalyticsProperties}
            />
          </div>
          {/* ShareBar at mobile sizes */}
          <div className="w-50 md_dn mh-auto">
            <ShareBar
              rectangularButtons={true}
              clipboard={false}
              liftOnHover={true}
              analyticsProperties={shareAnalyticsProperties}
            />
          </div>
        </div>
      </>
    );
  }, [event]);

  // --------- Description --------------
  const MainContentDescription = useMemo(() => {
    if (!event) return;
    return (
      <Card
        className="mt-5"
        style={{ boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px" }}
      >
        <h3 className="fwb">About {event.name}</h3>
        <p className="mb-0" style={{ whiteSpace: "pre-line" }}>
          {event.description}
        </p>
        {/* In Support Of section - shows when charity exists without an ID */}
        {event.charity && !event.charity.id && event.charity.name && (
          <>
            <h4 className="fwb mt-4">In Support Of</h4>
            <p className="org-name mb-0">{event.charity.name}</p>
          </>
        )}
      </Card>
    );
  }, [event]);

  // --------- User Actions -------------
  const UserActionsMenu = useMemo(() => {
    return (
      <>
        {event.isAdmin && (
          <div className="mb-5">
            <EditButton keyword={keyword} />
          </div>
        )}
        <div
          className="pb-4 flex bg-white flex-column"
          style={{
            border: "1px solid #e8e8e8",
            borderRadius: "3px",
          }}
        >
          <div onClick={handleFundraiserClick} className="ph-5 pv-4">
            <CardDetails
              allDay={event.allDay}
              title={event.name || ""}
              host={event.host || ""}
              location={event.location.name || ""}
              startAt={event.startAt || ""}
              endAt={event.endAt || ""}
              font={"proxima-nova"}
              timeZone={event.timeZone}
            />
          </div>
          {/* Goal and Progress Bar - See component for conditions in which it is displayed*/}
          {event.campaign && (
            <CardFundraisingGoal
              community={community}
              campaign={event.campaign}
              user={user}
            />
          )}

          {event.campaign && !disableDonation && !donationBufferExpired && (
            <div
              className="w-100 flex ph-4"
              style={{ justifyContent: "center", alignItems: "center" }}
            >
              <button
                onClick={() => {
                  googleAnalytics.event({
                    category: "Fundraiser Page",
                    action: "Donate Now Click",
                    label: `${event.id}`,
                  });
                  InspireAnalytics.track({
                    eventName: InspireAnalyticEvents.DONATE_NOW_CLICK,
                    properties: {
                      Page: window.location.pathname,
                      "Event ID": event.id,
                    },
                    mixpanel,
                    segment,
                  });
                  router.push(donateURL);
                }}
                className="ant-btn ant-btn-primary darkenPrimaryBkd bg-primary ttu f-2 mb-3 w-100"
                style={{
                  borderRadius: "9999px",
                  height: "44px",
                  border: "none",
                  boxShadow: "none",
                }}
              >
                Donate Now
              </button>
            </div>
          )}
          {/* COUNTDOWN TIMER */}
          {event.startAt && event.endAt && (
            <CountdownTimer
              disableDonation={disableDonation}
              startAt={event.startAt}
              endAt={event.endAt}
              endDateBuffer={eventEndDateBuffer}
            />
          )}
        </div>
      </>
    );
  }, [
    event,
    disableDonation,
    donationBufferExpired,
    googleAnalytics,
    mixpanel,
    segment,
    router,
    donateURL,
    keyword,
    community,
    user,
  ]);

  // --------- Donations Card -----------
  const DonationsCard = useMemo(() => {
    const isBenevolence = event.meta?.isBenevolence;
    const donationFeesCoverage =
      community.organization?.meta?.externalCampaign?.donationFeesCoverage;
    const donationFeesCoverageLogo =
      community.organization?.meta?.externalCampaign?.donationFeesCoverageLogo;
    const sponsorLogo = community?.meta?.brand?.logo || "";

    // Only show for fundraisers with a charity
    if (!event.isFundraiser || (!event.charity?.name && !isBenevolence)) {
      return null;
    }

    const imageUrl = isBenevolence
      ? "/images/divided_hearts.png"
      : "https://res.cloudinary.com/cotribute/image/upload/v1552409444/p/b94e770661dd465a887d22df66a04c61.png";

    const charityText = isBenevolence
      ? "support this need"
      : event.charity?.name;

    // Determine the logo to show for fee coverage
    const feesCoveredLogo = donationFeesCoverageLogo || sponsorLogo || null;

    return (
      <div
        style={{
          border: "1px solid #e8e8e8",
          borderRadius: "8px",
          boxShadow: "0 2px 4px -1px #e8e8e8",
        }}
        className="mt-4 mb-4 bg-white pa-4 flex items-center"
      >
        {/* Circular icon */}
        <div
          className="mr-4 flex-shrink-0"
          style={{
            width: "70px",
            height: "70px",
            borderRadius: "50%",
            border: "2px solid #7BA7BC",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <img
            style={{
              width: "45px",
              height: "45px",
              objectFit: "contain",
            }}
            src={imageUrl}
            alt="Donation Image"
          />
        </div>

        {/* Text content */}
        <div>
          <div className="gray-8 f-3">
            <span style={{ fontWeight: "700" }}>100% of your donation</span>{" "}
            will go to
          </div>
          <div style={{ fontStyle: "italic" }} className="gray-8 f-3">
            {charityText}
          </div>

          {/* Fees covered by - shown when there's a logo available */}
          {feesCoveredLogo && (
            <div className="flex items-center mt-2">
              <span className="f-2 gray-7 mr-2">Fees covered by:</span>
              <img
                src={feesCoveredLogo}
                alt="Sponsor Logo"
                style={{ height: "20px", objectFit: "contain" }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }, [event, community]);

  // --------- Latest Donations ---------
  const LatestDonationsCard = useMemo(() => {
    return (
      <div>
        <h3
          style={{
            borderBottom: "1px solid rgba(34, 34, 34, 0.05)",
          }}
          className="fwb pb-1 mt-4"
        >
          Latest Donations
        </h3>
        <div className="flex">
          <div className="w-100">
            <LatestDonations eventId={event.id} />
          </div>
        </div>
      </div>
    );
  }, []);

  // Determine if match grant is active
  const hasActiveMatchGrant =
    (event.campaign?.thresholdTrigger === "one_to_one_match_trigger" ||
      event.campaign?.thresholdTrigger === "donation_trigger" ||
      event.campaign?.thresholdTrigger === "tiered_match_trigger") &&
    (event.campaign?.matchFundsRemaining ?? 0) > 0;

  // **************************************************************
  // * RENDER THE VIEW
  // **************************************************************
  return (
    <>
      {/* Match Grant Banner - displays when match is active */}
      {event.campaign && (
        <MatchGrantBanner
          campaign={event.campaign}
          community={community}
          eventId={event.id}
          orgName={orgBootstrap.name}
          donationBufferExpired={donationBufferExpired}
        />
      )}
      <Group md="horizontal" spacing={5} style={{ minHeight: "600px" }}>
        {/* LEFT COLUMN */}
        <Group className="md_w-66">
          {MainContentHeaderComponent}
          {ShareComponent}
          {MainContentDescription}
        </Group>
        {/* RIGHT COLUMN */}
        <Group className="md_w-33 md-pl-5">
          {UserActionsMenu}
          {/* Animated arrows pointing to countdown when match is active */}
          <AnimatedArrows visible={hasActiveMatchGrant} />
          {DonationsCard}
          {/* Match Grant Milestones - shows unlocked thresholds */}
          {event.campaign && (
            <MatchGrantMilestones
              campaign={event.campaign}
              community={community}
            />
          )}
          {/* Thank You Notes - shows for admin after event ends */}
          {event.isAdmin && eventEnded && (
            <div className="mt-4 mb-4">
              <EventThankYouCard
                buttonPath={`/events/${event.id}/thankyou`}
                buttonTitle={THANK_YOU_NOTES_BUTTON_TITLE_DEFAULT}
                title={THANK_YOU_NOTES_TITLE_DEFAULT}
              />
            </div>
          )}
          {LatestDonationsCard}
        </Group>
      </Group>
    </>
  );
};

export default FundraiserContent;
